import React, { useRef, useState, useCallback, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import Modal from '../modal/Modal';
import UploadProgressBar from '../upload-progress/UploadProgressBar';
import { useUploadFailureDialog } from '../upload-progress/useUploadFailureDialog';
import SubmitModal from './SubmitModal';

import { POSITION, POSITION_MAP } from '../../constant/position';
import { useGetCurrentUserQuery } from '../../app/accounts/authuser';
import { useGetProjectMembershipQuery } from '../../app/accounts/memberships';
import { useSendProjectMessageMutation } from '../../app/services/projectMessages';
import useS3Upload, { UploadingFile, ValidationError } from '../../hooks/useS3Upload';

export interface PrimaryActionHandlerProps {
  onNavigateToTalkroom?: () => void;
}

export interface PrimaryActionHandlerRef {
  trigger: () => void;
}

/**
 * 主アクション（ProjectPrimaryAction のボタン）の実行部。ボタンからは ref の
 * trigger() で呼ぶ。PRODUCER は提出モーダル、CLIENT はファイル選択 →
 * トークルームへ送信。
 */
const PrimaryActionHandler = forwardRef<PrimaryActionHandlerRef, PrimaryActionHandlerProps>(({ onNavigateToTalkroom }, ref) => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showSubmitModal, setShowSubmitModal] = useState(false);

  // 提出完了時: モーダルを閉じて提出先ショットの PREVIEW ページへ遷移する
  const handleSubmitted = useCallback((shotId: string) => {
    setShowSubmitModal(false);
    if (projectId) navigate(`/${projectId}/${shotId}`);
  }, [projectId, navigate]);

  const { data: currentUserData } = useGetCurrentUserQuery();
  const { data: membershipData } = useGetProjectMembershipQuery(projectId!, {
    skip: !projectId || !currentUserData,
  });

  const userPosition = membershipData?.data?.position
    ? POSITION_MAP[membershipData.data.position] ?? null
    : null;

  // CLIENT/REVIEWER 用: S3 アップロード → トークルームにメッセージ送信
  const [sendMessage] = useSendProjectMessageMutation();

  const {
    uploadFiles,
    uploadingFiles,
    isUploading,
    abortFile,
    clearCompleted,
    retryFile,
    retryFiles,
    postProcessLabel,
    withPostProcess,
  } = useS3Upload({
    purpose: 'project_message',
    projectId,
    onValidationError: (errors: ValidationError[]) => {
      errors.forEach((e) => console.warn(`バリデーションエラー: ${e.file.name} - ${e.message}`));
    },
  });

  // アップロード部分失敗時の「再試行 / 失敗分を除いて続行 / キャンセル」ダイアログ
  const { resolveUploadFailures, uploadFailureDialog } = useUploadFailureDialog({
    retryFiles,
  });

  // アップロード完了後に自動クリア。
  // エラー行が残っている場合は自動クリアしない（失敗ダイアログの選択で解決する）
  useEffect(() => {
    if (uploadingFiles.length === 0) return;

    const allDone = uploadingFiles.every((f) => f.status === 'completed');

    if (allDone) {
      const timer = setTimeout(() => {
        clearCompleted();
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [uploadingFiles, clearCompleted]);

  const handleFilesSelected = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files || files.length === 0 || !projectId) return;

      const fileArray = Array.from(files);

      try {
        const uploadResults = await uploadFiles(fileArray);

        // 失敗があれば「再試行 / 失敗分を除いて続行 / キャンセル」ダイアログで解決
        const hadFailures = uploadResults.some((f) => f.status === 'error');
        const resolved = await resolveUploadFailures(uploadResults);
        if (resolved === null) {
          // キャンセル: 進捗を畳んで中断
          clearCompleted();
          if (fileInputRef.current) fileInputRef.current.value = '';
          return;
        }
        if (hadFailures) clearCompleted();

        const fileKeys = resolved
          .filter((f: UploadingFile) => f.fileKey)
          .map((f: UploadingFile) => f.fileKey as string);

        if (fileKeys.length > 0) {
          // 送信が終わるまでは離脱を引き止める
          await withPostProcess(
            () =>
              sendMessage({
                project_id: projectId,
                message: '',
                file_keys: fileKeys,
              }).unwrap(),
            'メッセージを送信中',
          );
          onNavigateToTalkroom?.();
        }
      } catch (err) {
        console.error('ファイルアップロードエラー:', err);
      }

      // input をリセットして同じファイルを再選択可能にする
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [projectId, uploadFiles, resolveUploadFailures, clearCompleted, sendMessage, withPostProcess, onNavigateToTalkroom],
  );

  useImperativeHandle(ref, () => ({
    trigger: () => {
      if (userPosition === POSITION.CLIENT) {
        fileInputRef.current?.click();
      } else if (userPosition === POSITION.PRODUCER) {
        setShowSubmitModal(true);
      }
    },
  }), [userPosition]);

  return (
    <>
      {/* CLIENT/REVIEWER 用: 非表示ファイル選択 */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        style={{ display: 'none' }}
        onChange={handleFilesSelected}
      />

      {/* CLIENT/REVIEWER 用: アップロード進捗モーダル */}
      {uploadingFiles.length > 0 && (
        <Modal
          onClose={() => {
            if (!isUploading) clearCompleted();
          }}
          title="ファイルアップロード"
          size="default"
        >
          <UploadProgressBar
            files={uploadingFiles}
            onCancel={abortFile}
            onRetry={(file) => void retryFile(file.id)}
            compact={false}
            fitToParent
            postProcessLabel={postProcessLabel}
          />
        </Modal>
      )}

      {/* アップロード部分失敗ダイアログ */}
      {uploadFailureDialog}

      {/* PRODUCER/DIRECTOR 用: 提出モーダル */}
      {showSubmitModal && projectId && (
        <SubmitModal
          projectId={projectId}
          onClose={() => setShowSubmitModal(false)}
          onSubmitted={handleSubmitted}
        />
      )}
    </>
  );
});

PrimaryActionHandler.displayName = 'PrimaryActionHandler';

export default PrimaryActionHandler;
